import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Button, Row, Col, Space, Typography, Tag, Alert, Slider, message, Progress, Tooltip } from 'antd';
import { ReloadOutlined, WarningOutlined, LinkOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/pt-br';
import api from '../services/api';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');
const { Title, Text, Paragraph } = Typography;

const AtRiskTeams = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [minScore, setMinScore] = useState(0.15);
  const [limit, setLimit] = useState(30);
  const [minConv, setMinConv] = useState(1);
  const [details, setDetails] = useState({}); // { [customer_id]: {loading, data} }

  const loadDetails = useCallback(async (customerId) => {
    if (details[customerId]?.data || details[customerId]?.loading) return;
    setDetails(d => ({ ...d, [customerId]: { loading: true } }));
    try {
      const r = await api.get(`/monitoring/at-risk/${customerId}/details`);
      setDetails(d => ({ ...d, [customerId]: { loading: false, data: r.data } }));
    } catch (err) {
      setDetails(d => ({ ...d, [customerId]: { loading: false, error: err.message } }));
    }
  }, [details]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/monitoring/at-risk', { params: { limit, min_score: minScore, min_conversations: minConv } });
      setData(r.data);
    } catch (err) {
      message.error('Erro: ' + (err.response?.data?.error || err.message));
    } finally { setLoading(false); }
  }, [limit, minScore, minConv]);

  useEffect(() => { load(); }, [load]);

  const scoreColor = (s) => s >= 0.5 ? '#ff4d4f' : s >= 0.35 ? '#faad14' : '#1890ff';

  const columns = [
    { title: '#', width: 50, render: (_, __, i) => i + 1 },
    { title: 'Time', dataIndex: 'game_name', width: 220,
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <a href={r.admin_link} target="_blank" rel="noreferrer">{v || '(sem nome)'}</a>
          <Text type="secondary" style={{ fontSize: 11 }}>id {r.customer_id} • {r.cellphone}</Text>
        </Space>
      ),
    },
    { title: 'Risk Score', dataIndex: 'risk_score', width: 220, align: 'center',
      sorter: (a, b) => a.risk_score - b.risk_score,
      defaultSortOrder: 'descend',
      render: (v, r) => (
        <Tooltip title={
          `Raw: ${(r.raw_risk_score * 100).toFixed(0)}%\nRecovery: -${(r.recovery_factor * 100).toFixed(0)}%\nAjustado: ${(v * 100).toFixed(0)}%`
        }>
          <Progress percent={Math.round(v * 100)} strokeColor={scoreColor(v)} format={() => `${(v*100).toFixed(0)}%`} />
          {r.recovery_factor > 0.1 && (
            <Text type="secondary" style={{ fontSize: 10 }}>raw {(r.raw_risk_score * 100).toFixed(0)}% -{(r.recovery_factor * 100).toFixed(0)}%</Text>
          )}
        </Tooltip>
      ),
    },
    { title: 'Sinais saudáveis', width: 180, align: 'center',
      render: (_, r) => (
        <Space size={4} wrap>
          {r.subscription_ok && <Tag color="green" style={{ margin: 0, fontSize: 10 }}>💰pag</Tag>}
          {r.recent_games_ok && <Tag color="green" style={{ margin: 0, fontSize: 10 }}>⚽conv OK</Tag>}
          {r.wa_reply_recent && <Tag color="green" style={{ margin: 0, fontSize: 10 }}>💬WA {'<'}30d</Tag>}
          {r.days_since_last_signal !== null && r.days_since_last_signal > 60 && (
            <Tag color="green" style={{ margin: 0, fontSize: 10 }}>📅{Math.round(r.days_since_last_signal)}d s/ queixa</Tag>
          )}
          {r.days_since_last_signal !== null && r.days_since_last_signal <= 30 && (
            <Tag color="red" style={{ margin: 0, fontSize: 10 }}>⚠️queixa há {Math.round(r.days_since_last_signal)}d</Tag>
          )}
        </Space>
      ),
    },
    { title: 'Conversas', dataIndex: 'conversations_analyzed', width: 100, align: 'center' },
    { title: 'Trial?', dataIndex: 'trial', width: 80, align: 'center',
      render: (v) => v ? <Tag color="orange">TRIAL</Tag> : <Tag color="green">PAGO</Tag>,
    },
    { title: 'Grupo sync', dataIndex: 'group_synced', width: 100, align: 'center',
      render: (v) => v ? <Tag color="green">SIM</Tag> : <Tag color="red">NÃO</Tag>,
    },
    { title: 'Última resp.', dataIndex: 'last_whatsapp_reply', width: 140,
      render: (v) => v
        ? <Tooltip title={dayjs(v).format('DD/MM HH:mm')}><Text style={{ fontSize: 11 }}>{dayjs(v).fromNow()}</Text></Tooltip>
        : <Text type="secondary" style={{ fontSize: 11 }}>nunca</Text>,
    },
    { title: '', width: 60, align: 'center',
      render: (_, r) => <a href={r.admin_link} target="_blank" rel="noreferrer"><LinkOutlined /></a>,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <WarningOutlined /> Times em risco de churn
          </Title>
          <Text type="secondary">
            Ranking baseado em similaridade das conversas Chatwoot com times que já saíram (PLANO_EXPIRADO + CANCELADO).
          </Text>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Recalcular</Button>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card size="small" title={`Score mínimo (${minScore.toFixed(2)})`}>
            <Slider min={0.05} max={0.8} step={0.05} value={minScore} onChange={setMinScore}
              marks={{ 0.05: '0.05', 0.15: '0.15', 0.3: '0.30', 0.5: '0.50', 0.8: '0.80' }} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small" title={`Min conversas (${minConv})`}>
            <Slider min={1} max={10} step={1} value={minConv} onChange={setMinConv}
              marks={{ 1: '1', 3: '3', 5: '5', 10: '10' }} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small" title={`Máximo de times (${limit})`}>
            <Slider min={5} max={50} step={5} value={limit} onChange={setLimit}
              marks={{ 5: '5', 20: '20', 50: '50' }} />
          </Card>
        </Col>
      </Row>

      {data && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${data.teams_at_risk?.length || 0} times identificados`}
          description={`Calculado em ${data.took_ms}ms usando ${data.neighbors_k} vizinhos por embedding. Score = churn_ratio × similarity_to_churn.`}
        />
      )}

      <Card>
        <Table
          rowKey="customer_id"
          loading={loading}
          dataSource={data?.teams_at_risk || []}
          columns={columns}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          expandable={{
            onExpand: (expanded, record) => { if (expanded) loadDetails(record.customer_id); },
            expandedRowRender: (record) => {
              const det = details[record.customer_id];
              if (!det || det.loading) return <Text type="secondary">carregando conversas...</Text>;
              if (det.error) return <Alert type="error" message={det.error} />;
              const convs = det.data?.conversations || [];
              if (convs.length === 0) return <Text type="secondary">sem conversas embeddadas</Text>;
              const substantive = convs.filter(c => !c.is_operational).length;
              const operational = convs.length - substantive;
              return (
                <div style={{ padding: '8px 24px', background: '#fafafa' }}>
                  <Space>
                    <Text strong>{convs.length} conversas</Text>
                    <Tag color="blue">{substantive} substantivas</Tag>
                    {operational > 0 && <Tag color="default">{operational} operacionais (bot/confirm)</Tag>}
                  </Space>
                  {convs.map(c => (
                    <Card key={c.conversation_id} size="small" style={{ marginTop: 8, opacity: c.is_operational ? 0.55 : 1 }} bodyStyle={{ padding: 12 }}>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Space direction="vertical" size={4} style={{ width: '100%' }}>
                            <Space wrap>
                              <a href={c.chatwoot_link} target="_blank" rel="noreferrer">
                                <LinkOutlined /> Conv #{c.conversation_id}
                              </a>
                              <Text type="secondary" style={{ fontSize: 11 }}>({c.message_count} msgs • {c.status})</Text>
                              {c.is_operational && <Tag color="default">OPERACIONAL</Tag>}
                              {(c.tags || []).map(t => <Tag key={t.label} color={t.color}>{t.label}</Tag>)}
                            </Space>
                            <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>
                              {c.preview}
                            </Text>
                          </Space>
                        </Col>
                        <Col span={12}>
                          {c.top_churn_neighbor ? (
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                              <Space>
                                <Tag color="red">SIMILAR A CHURN</Tag>
                                <Text style={{ fontSize: 12 }}>
                                  sim <strong>{(c.top_churn_neighbor.similarity * 100).toFixed(0)}%</strong> com time #{c.top_churn_neighbor.neighbor_customer_id} ({c.top_churn_neighbor.status})
                                </Text>
                              </Space>
                              <a href={c.top_churn_neighbor.chatwoot_link} target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>
                                <LinkOutlined /> Ver conv similar #{c.top_churn_neighbor.conversation_id}
                              </a>
                              <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'pre-wrap', background: '#fff2f0', padding: 8, borderRadius: 4 }}>
                                {c.top_churn_neighbor.preview}
                              </Text>
                            </Space>
                          ) : (
                            <Text type="secondary" style={{ fontSize: 11 }}>sem vizinho de churn com similaridade suficiente</Text>
                          )}
                        </Col>
                      </Row>
                    </Card>
                  ))}
                </div>
              );
            },
          }}
        />
      </Card>

      <Paragraph type="secondary" style={{ marginTop: 16, fontSize: 12 }}>
        <strong>Como ler:</strong> Score alto = as msgs do organizador no Chatwoot são semanticamente parecidas com as msgs de times que já saíram.
        Não é predição determinística — é um sinal de atenção. Recomendado abordar proativamente times com score {'>'}=0.5.
      </Paragraph>
    </div>
  );
};

export default AtRiskTeams;
